// PrivateRoute.tsx
import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import LoadingSpinner from '@/components/LoadingSpinner';
import { fetchMissingConsents } from '@/features/terms/model/missing-consents';
import { toLangType } from '@/entities/terms/types';
import { consentGateHref } from '@/entities/terms';
import { getLocale } from '@/i18n';

import { useAuth } from './AuthContext';

interface PrivateRouteProps {
  /** 기본은 <Outlet/> — 이 라우트는 이제 MainLayout **안에** 중첩되므로(router.tsx) 별도
   * 레이아웃을 직접 그리지 않는다. 과거엔 여기서 `<MainLayout/>`을 직접 그렸는데, 그러면
   * router.tsx의 두 자리(공개 라우트 branch / 이 PrivateRoute branch)에 `<MainLayout/>`이
   * 각각 별도 엘리먼트로 존재해 그 사이를 넘나드는 네비게이션마다(예: 홈→내 자산) MainLayout
   * 전체가 unmount+remount됐다. Header의 실시간 시세 WebSocket 구독도 그 안에서 같이
   * unmount+resubscribe되며 워커의 실제 WebSocket 연결이 한 번 끊겼다 재연결됐다
   * (~3초, 「내 자산 진입 후 잠깐 끊긴다」 증상의 근본 원인 — 실측 3/3 재현, gap 3044~3058ms).
   * MainLayout을 트리에서 한 자리로 합치면(이제 이 라우트를 그 안에 중첩) 이 unmount 자체가
   * 사라진다. */
  layout?: React.ReactNode;
}

const PrivateRoute = ({ layout }: PrivateRouteProps) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) return;

    // TRAD-3759 — 종전엔 하우스 룰(EULA) 하나만 봤다. 이제 «빠진 동의 목록»(required-missing + 하우스 룰)이 비어 있어야 통과.
    // 재동의 화면(/terms-agreement)과 같은 함수를 쓴다 — 목록을 따로 만들면 «게이트는 막는데 화면엔 항목이 없다»가 된다.
    fetchMissingConsents(toLangType(getLocale()))
      .then(missing => {
        setTermsAccepted(missing.length === 0);
        setTermsChecked(true);
      })
      .catch(error => {
        // G2(팀장 결정 2026-10-18) — 조회 실패 시 막지 않는다(통과). 단 삼키지 않고 로깅한다(종전엔 로그 없이 통과였다).
        console.error('[PrivateRoute] consent status failed — letting through', error);
        setTermsChecked(true);
      });
  }, [isAuthenticated]);

  // 인증 상태 확인 중
  if (isAuthenticated === null) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  // 미인증
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 약관 동의 확인 중
  if (!termsChecked) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  // 약관 미동의
  if (!termsAccepted) {
    // G7 — 동의 뒤 원래 화면으로 돌아오게 지금 주소를 from 으로 넘긴다(내부 경로만 — safeReturnPath).
    return <Navigate to={consentGateHref(location.pathname + location.search)} replace />;
  }

  return layout ?? <Outlet />;
};

export default PrivateRoute;
